import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { TableColumn } from '../types/table';

export interface UseTableSearchOptions<T = any> {
  columns: TableColumn<T>[];
  initialSearchColumn?: string;
  onSearchChange?: (query: string, column: string) => void;
}

export interface UseTableSearchReturn<T = any> {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchColumn: string;
  setSearchColumn: (column: string) => void;
  lastClickedColumn: string;
  handleColumnClick: (columnKey: string) => void;
  searchPlaceholder: string;
  activeSearchColumn: TableColumn<T> | undefined;
}

/**
 * Custom hook for managing table search functionality
 * Provides state and handlers for click-to-change-search-column behavior
 *
 * @param options - Configuration options
 * @returns Search state and handlers
 *
 * @example
 * ```tsx
 * const {
 *   searchQuery,
 *   setSearchQuery,
 *   searchColumn,
 *   handleColumnClick,
 *   searchPlaceholder
 * } = useTableSearch({
 *   columns: tableColumns,
 *   onSearchChange: (query, column) => fetchData(query, column)
 * });
 * ```
 */
export function useTableSearch<T = any>({
  columns,
  initialSearchColumn,
  onSearchChange,
}: UseTableSearchOptions<T>): UseTableSearchReturn<T> {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchColumn, setSearchColumn] = useState(initialSearchColumn || '');
  const [lastClickedColumn, setLastClickedColumn] = useState('');

  // Initialize search column to first searchable column if not set
  useEffect(() => {
    if (!searchColumn && columns.length > 0) {
      const firstSearchable = columns.find(col => col.searchable !== false);
      if (firstSearchable) {
        setSearchColumn(firstSearchable.key);
      }
    }
  }, [columns, searchColumn]);

  // Handle column click - updates active search column
  const handleColumnClick = useCallback((columnKey: string) => {
    const column = columns.find(col => col.key === columnKey);
    if (column && column.searchable !== false) {
      setLastClickedColumn(columnKey);
      setSearchColumn(columnKey);
      // Don't call onSearchChange here - let the useEffect handle it
      // This prevents double calls and race conditions
    }
  }, [columns]);

  // Call parent callback when search changes
  // Use a ref to prevent duplicate calls when both query and column change simultaneously
  const prevSearchRef = useRef({ query: searchQuery, column: searchColumn });
  
  useEffect(() => {
    if (onSearchChange && searchColumn) {
      // Only call if either query or column actually changed
      if (prevSearchRef.current.query !== searchQuery || prevSearchRef.current.column !== searchColumn) {
        prevSearchRef.current = { query: searchQuery, column: searchColumn };
        onSearchChange(searchQuery, searchColumn);
      }
    }
  }, [searchQuery, searchColumn, onSearchChange]);

  // Get the active search column object
  const activeSearchColumn = useMemo(() => {
    const clickedCol = columns.find(col => col.key === lastClickedColumn);
    const currentCol = columns.find(col => col.key === searchColumn);
    return clickedCol || currentCol;
  }, [columns, lastClickedColumn, searchColumn]);

  // Generate dynamic placeholder text
  const searchPlaceholder = useMemo(() => {
    if (activeSearchColumn) {
      return `Search ${activeSearchColumn.label.toLowerCase()}...`;
    }
    return 'Search records...';
  }, [activeSearchColumn]);

  return {
    searchQuery,
    setSearchQuery,
    searchColumn,
    setSearchColumn,
    lastClickedColumn,
    handleColumnClick,
    searchPlaceholder,
    activeSearchColumn,
  };
}
